import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { RegistrationCampaign } from "@/types";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";

export default function Campaigns() {
  const [items, setItems] = useState<RegistrationCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  // Form nuova campagna
  const [creating, setCreating] = useState(false);
  const [nome, setNome] = useState("");
  const [fonte, setFonte] = useState("");
  const [richiedeApprovazione, setRichiedeApprovazione] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // QR full-screen
  const [qrCampaign, setQrCampaign] = useState<RegistrationCampaign | null>(null);

  function load() {
    setLoading(true);
    api.get<RegistrationCampaign[]>("/admin/campaigns")
      .then(setItems)
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function create(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null); setSaving(true);
    try {
      await api.post("/admin/campaigns", {
        nome: nome.trim(),
        fonte: fonte.trim(),
        richiedeApprovazione,
      });
      setNome(""); setFonte(""); setRichiedeApprovazione(true);
      setCreating(false);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggle(id: string) {
    await api.post(`/admin/campaigns/${id}/toggle`);
    load();
  }

  async function del(id: string, nome: string) {
    if (!confirm(`Eliminare la campagna "${nome}"? I contatti già iscritti restano in anagrafica.`)) return;
    await api.delete(`/admin/campaigns/${id}`);
    load();
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-5">
      <header className="flex justify-between items-baseline">
        <h1 className="text-3xl font-serif text-primary">Campagne di registrazione</h1>
        <Button variant="secondary" onClick={() => setCreating(!creating)}>
          {creating ? "Annulla" : "+ Nuova campagna"}
        </Button>
      </header>

      {creating && (
        <Card>
          <form onSubmit={create} className="space-y-3">
            <Input label="Nome (visibile a te, es. 'Open Day Marzo 2026')"
              value={nome} onChange={(e) => setNome(e.target.value)} required />
            <Input label='Fonte (label tracciata sul contatto, es. "open-day-marzo-26")'
              value={fonte} onChange={(e) => setFonte(e.target.value)} required />
            <label className="flex items-center gap-2 text-sm text-secondary">
              <input type="checkbox" checked={richiedeApprovazione}
                onChange={(e) => setRichiedeApprovazione(e.target.checked)} />
              Richiede approvazione manuale prima di essere attivo
            </label>
            {error && <div className="text-red-300 text-sm">{error}</div>}
            <Button type="submit" loading={saving}>Crea campagna</Button>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      ) : items.length === 0 ? (
        <Card>
          <p className="text-secondary text-sm">
            Nessuna campagna. Creane una per generare un QR di registrazione self-service.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <Card key={c.id}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {c.attiva
                      ? <Badge color="#2A7A4B">attiva</Badge>
                      : <Badge color="#555577">chiusa</Badge>}
                    <Badge variant="outline" color="#5B3E8A">fonte: {c.fonte}</Badge>
                    {!c.richiedeApprovazione && (
                      <Badge color="#E8613C">auto-approva</Badge>
                    )}
                  </div>
                  <div className="text-primary text-lg font-serif">{c.nome}</div>
                  <div className="text-xs text-muted font-mono mt-1">
                    {c.nIscritti} iscritti
                    {c.nPending > 0 && <span className="text-accent"> · {c.nPending} in attesa</span>}
                    {" · creata "}
                    {new Date(c.createdAt).toLocaleDateString("it-IT")}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setQrCampaign(c)}
                    className="bg-purple text-white px-3 py-2 rounded-xl font-mono text-xs hover:brightness-110">
                    📱 Mostra QR
                  </button>
                  <button onClick={() => toggle(c.id)}
                    className={`px-3 py-2 rounded-xl font-mono text-xs hover:brightness-110 ${
                      c.attiva ? "bg-accent text-white" : "bg-green text-white"
                    }`}>
                    {c.attiva ? "Chiudi" : "Riapri"}
                  </button>
                  <button onClick={() => del(c.id, c.nome)}
                    className="bg-card border border-border text-secondary px-3 py-2 rounded-xl font-mono text-xs hover:border-red-500 hover:text-red-300">
                    Elimina
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {qrCampaign && (
        <CampaignQROverlay
          campaign={qrCampaign}
          onClose={() => setQrCampaign(null)}
        />
      )}
    </div>
  );
}

// ─── Overlay full-screen per QR (per proiettore in aula) ──────────────

function CampaignQROverlay({
  campaign,
  onClose,
}: {
  campaign: RegistrationCampaign;
  onClose: () => void;
}) {
  const [pngUrl, setPngUrl] = useState<string | null>(null);

  useEffect(() => {
    const jwt = localStorage.getItem("jwt");
    fetch(`${import.meta.env.VITE_API_URL}/admin/campaigns/${campaign.id}/qr.png`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then((r) => r.ok ? r.blob() : null)
      .then((b) => setPngUrl(b ? URL.createObjectURL(b) : null));

    // Esc per chiudere
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [campaign.id, onClose]);

  const base = (import.meta.env.VITE_API_URL ?? "").replace(/\/api$/, "");
  const publicUrl = `${base}/registrati/${campaign.slug}`;

  return (
    <div className="fixed inset-0 z-50 bg-bg flex flex-col items-center justify-center px-4 py-6">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-secondary hover:text-primary text-2xl leading-none w-10 h-10 flex items-center justify-center"
        aria-label="Chiudi"
      >
        ✕
      </button>

      <div className="text-center mb-6 max-w-2xl">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-secondary mb-2">
          Registrazione · {campaign.fonte}
        </div>
        <h2 className="text-4xl md:text-5xl text-primary font-serif">{campaign.nome}</h2>
        <p className="text-secondary mt-3">
          Scansiona il QR con la fotocamera del telefono per registrarti
        </p>
      </div>

      {pngUrl ? (
        <img src={pngUrl} alt="QR Code"
          className="w-[min(70vmin,500px)] h-[min(70vmin,500px)] bg-white p-4 rounded-2xl" />
      ) : (
        <div className="w-[min(70vmin,500px)] h-[min(70vmin,500px)] bg-white rounded-2xl flex items-center justify-center">
          <Spinner size={48} />
        </div>
      )}

      <div className="mt-6 font-mono text-xs text-muted text-center">
        {publicUrl}
      </div>
      <div className="mt-2 font-mono text-xs text-secondary">
        Premi <kbd className="bg-card border border-border px-1.5 py-0.5 rounded">Esc</kbd> per chiudere
      </div>
    </div>
  );
}
